/**
 * Research landing page — /data
 * Design: Dark glassmorphism, featured research card with stats, CTA
 */
import React, { useState } from "react";
import SiteLayout from "@/components/SiteLayout";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { useIsMobile } from "@/hooks/useIsMobile";
import { VIDEO_REVIEW_URL as STRATEGIST_URL } from "@/lib/cta";

const ff = "'Barlow', 'Barlow Fallback', sans-serif";
const cyan = "#57d7e2";

interface ResearchPiece {
  title: string;
  date: string;
  description: string;
  href: string;
  badge: string;
  stats: { value: string; label: string }[];
}

const RESEARCH: ResearchPiece[] = [
  {
    title: "Where AI Overviews Actually Appear: 11 Industries, 317 Keywords",
    date: "September 2026",
    description:
      "AI Overviews are not spreading evenly. Across 11 industries and 317 measured US keywords, presence runs from 0% in home services to 96.7% in B2B accounting software — and on the local-intent queries an SMB competes for, we did not record a single one.",
    href: "/studies/ai-overview-by-industry-2026",
    badge: "Original Research",
    stats: [
      { value: "52.4%", label: "of 317 US keywords returned an AI Overview" },
      { value: "0%", label: 'of the 23 "near me" keywords we measured' },
      { value: "11", label: "industries analyzed" },
    ],
  },
  {
    title: "The AI Overview Effect: How Google's AI Is Reshaping B2B Search in 2026",
    date: "March 2026",
    description: "An analysis of organic click-through rates across 50 B2B keywords reveals a consistent and material gap between keywords where AI Overviews appear and those where they don't — and it points clearly to which categories of B2B content are most at risk.",
    href: "/studies/ctr-study-2026",
    badge: "Original Research",
    stats: [
      { value: "84%", label: "of B2B keywords trigger an AI Overview" },
      { value: "10pp", label: "organic CTR drop with AI Overviews" },
      { value: "50", label: "B2B keywords analyzed" },
    ],
  },
];

function ResearchCard({ piece, isMobile }: { piece: ResearchPiece; isMobile: boolean }) {
  const [hov, setHov] = useState(false);
  return (
    <a
      href={piece.href}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "block",
        textDecoration: "none",
        border: `1px solid ${hov ? "rgba(87,215,226,0.3)" : "rgba(255,255,255,0.07)"}`,
        borderRadius: "20px",
        background: hov ? "rgba(87,215,226,0.04)" : "#111",
        overflow: "hidden",
        transition: "border-color 0.2s, background 0.2s, transform 0.2s",
        transform: hov ? "translateY(-2px)" : "translateY(0)",
      }}
    >
      {/* Top section: badge + date + title + description */}
      <div style={{ padding: isMobile ? "32px 24px 28px" : "44px 48px 36px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px", flexWrap: "wrap" }}>
          <span style={{
            display: "inline-block", padding: "4px 12px",
            borderRadius: "4px", fontSize: "11px", fontWeight: 700,
            fontFamily: ff, letterSpacing: "0.08em", textTransform: "uppercase",
            color: cyan, background: "rgba(87,215,226,0.1)",
            border: "1px solid rgba(87,215,226,0.2)",
          }}>
            {piece.badge}
          </span>
          <span style={{ color: "rgba(255,255,255,0.35)", fontSize: "13px", fontFamily: ff }}>{piece.date}</span>
        </div>
        <h2 style={{
          color: "white",
          fontSize: isMobile ? "24px" : "clamp(26px, 2.8vw, 34px)",
          fontWeight: 800,
          fontFamily: ff,
          lineHeight: 1.15,
          letterSpacing: "-0.02em",
          marginBottom: "16px",
        }}>
          {piece.title}
        </h2>
        <p style={{
          color: "rgba(255,255,255,0.5)",
          fontSize: "16px",
          fontFamily: ff,
          lineHeight: 1.7,
          marginBottom: "24px",
          maxWidth: "680px",
        }}>
          {piece.description}
        </p>
        <span style={{
          color: cyan,
          fontSize: "14px",
          fontWeight: 600,
          fontFamily: ff,
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
        }}>
          Read the full study
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </div>

      {/* Bottom stat strip */}
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : `repeat(${piece.stats.length}, 1fr)`,
        borderTop: "1px solid rgba(255,255,255,0.06)",
      }}>
        {piece.stats.map((stat, i) => (
          <div key={i} style={{
            padding: isMobile ? "20px 24px" : "24px 48px",
            borderLeft: (!isMobile && i > 0) ? "1px solid rgba(255,255,255,0.06)" : "none",
            borderTop: (isMobile && i > 0) ? "1px solid rgba(255,255,255,0.06)" : "none",
          }}>
            <div style={{ color: cyan, fontSize: "28px", fontWeight: 700, fontFamily: ff, lineHeight: 1, marginBottom: "6px" }}>
              {stat.value}
            </div>
            <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "13px", fontFamily: ff, lineHeight: 1.4 }}>
              {stat.label}
            </div>
          </div>
        ))}
      </div>
    </a>
  );
}

export default function ResearchIndex() {
  const isMobile = useIsMobile();

  useSeoMeta({
    title: "Original SEO Research & Studies 2026 — Indexed",
    description: "Original SEO studies from Indexed — data-driven research on AI Overviews, organic CTR, B2B search behavior, and the evolving SERP landscape. Free to read.",
    canonical: "/studies",
    ogType: "website",
    jsonLd: [{
      "@type": "CollectionPage",
      "name": "Indexed SEO Research & Studies",
      "description": "Original SEO research from Indexed covering AI Overviews, CTR benchmarks, SERP features, and B2B search trends.",
      "url": "https://www.joinindexed.com/studies",
      "publisher": { "@type": "Organization", "name": "Indexed", "url": "https://www.joinindexed.com" }
    }],
  });

  return (
    <SiteLayout>
      {/* Hero */}
      <section style={{
        background: "#0a0a0a",
        padding: isMobile ? "160px 20px 60px" : "200px 72px 80px",
      }}>
        <div style={{ maxWidth: "900px", margin: "0 auto", textAlign: "center" }}>
          <h1 style={{
            color: "white",
            fontSize: isMobile ? "34px" : "clamp(44px, 5vw, 60px)",
            fontWeight: 700,
            fontFamily: ff,
            lineHeight: 1.08,
            letterSpacing: "-0.02em",
            marginBottom: "20px",
          }}>
            Data that changes <span style={{ color: cyan }}>how you think about search.</span>
          </h1>
          <p style={{
            color: "rgba(255,255,255,0.5)",
            fontSize: "18px",
            fontFamily: ff,
            lineHeight: 1.6,
            maxWidth: "620px",
            margin: "0 auto",
          }}>
            Original studies and analysis from Indexed on SEO, search performance, and B2B organic growth. Backed by real keyword data — not opinions.
          </p>
        </div>
      </section>

      {/* Research cards */}
      <section style={{
        background: "#0a0a0a",
        padding: isMobile ? "0 20px 60px" : "0 72px 80px",
      }}>
        <div style={{ maxWidth: "820px", margin: "0 auto", display: "flex", flexDirection: "column", gap: "24px" }}>
          {RESEARCH.map(piece => (
            <ResearchCard key={piece.href} piece={piece} isMobile={isMobile} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{
        background: "#0c0c0c",
        borderTop: "1px solid rgba(255,255,255,0.05)",
        padding: isMobile ? "60px 20px" : "80px 72px",
      }}>
        <div style={{ maxWidth: "640px", margin: "0 auto", textAlign: "center" }}>
          <h2 style={{
            color: "white",
            fontSize: isMobile ? "26px" : "36px",
            fontWeight: 700,
            fontFamily: ff,
            lineHeight: 1.15,
            marginBottom: "16px",
          }}>
            Want this analysis for your keywords?
          </h2>
          <p style={{
            color: "rgba(255,255,255,0.5)",
            fontSize: "17px",
            fontFamily: ff,
            lineHeight: 1.6,
            marginBottom: "32px",
          }}>
            We run custom AI Overview exposure audits for B2B companies. See exactly which of your keywords are affected — and what it's costing you.
          </p>
          <a href={STRATEGIST_URL} style={{
            display: "inline-flex", alignItems: "center", gap: "8px",
            padding: "16px 32px",
            background: "linear-gradient(135deg, #85e2e9 0%, #20b0bc 100%)",
            borderRadius: "88px", border: "1px solid #57d7e2",
            boxShadow: "0px 4px 20px rgba(87,215,226,0.25)",
            textDecoration: "none",
            color: "#0a0c0c", fontSize: "15px", fontWeight: 700, fontFamily: ff,
            transition: "opacity 0.2s, transform 0.2s",
          }}
            onMouseEnter={e => { e.currentTarget.style.opacity = "0.88"; e.currentTarget.style.transform = "translateY(-1px)"; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "translateY(0)"; }}
          >
            Get a custom analysis
          </a>
        </div>
      </section>
    </SiteLayout>
  );
}
